/**
 * Markets Service
 *
 * API client for site markets configuration and demand research data.
 *
 * Date: 2026-01-26
 */

import { authFetch } from '@/utils/auth-fetch';

export interface Market {
  id: string;
  language: string;
  location: string;
  locationCode: string | null;
  currency: string;
  isPrimary: boolean;
  highDemandThreshold: number;
  lowDemandThreshold: number;
  exchangeRateToPrimary: number;
}

export interface CategoryDemandReadiness {
  category: string;
  totalProducts: number;
  productsWithContent: number;
  productsWithKeywordData: number;
  searchValue: number;
  avgCpc: number;
  totalSearches: number;
  readinessPct: number;
  keywordCoveragePct: number;
  quadrant: 'TOP_PRIORITY' | 'PROTECT' | 'DEVELOP' | 'OPTIMIZE' | 'DEPRIORITIZE';
  priority: number;
  opportunityGap: number;
}

export interface DemandReadinessResponse {
  market: {
    language: string;
    location: string;
    locationCode: string;
    currency: string;
    highDemandThreshold: number;
    lowDemandThreshold: number;
  };
  categories: CategoryDemandReadiness[];
  thresholds: {
    highDemand: number;
    lowDemand: number;
    highReadiness: number;
    lowReadiness: number;
  };
}

export interface MarketOpportunitySummary {
  market: Market;
  summary: {
    totalMarketValue: number;
    capturedValue: number;
    opportunityGap: number;
    captureRate: number;
    totalCategories: number;
    categoriesWithData: number;
  };
  quadrantCounts: {
    TOP_PRIORITY: number;
    PROTECT: number;
    DEVELOP: number;
    OPTIMIZE: number;
    DEPRIORITIZE: number;
  };
  topOpportunities: CategoryDemandReadiness[];
  protectCategories: CategoryDemandReadiness[];
  allCategories: Array<{
    category: string;
    x: number;
    y: number;
    quadrant: string;
    opportunityGap: number;
    totalProducts: number;
  }>;
}

// Location codes for Google Ads API
export const LOCATION_CODES: Record<string, string> = {
  // Western Europe
  'Spain': 'geoTargetConstants/2724',
  'United Kingdom': 'geoTargetConstants/2826',
  'France': 'geoTargetConstants/2250',
  'Germany': 'geoTargetConstants/2276',
  'Italy': 'geoTargetConstants/2380',
  'Portugal': 'geoTargetConstants/2620',
  'Netherlands': 'geoTargetConstants/2528',
  'Belgium': 'geoTargetConstants/2056',
  'Ireland': 'geoTargetConstants/2372',
  'Austria': 'geoTargetConstants/2040',
  'Switzerland': 'geoTargetConstants/2756',
  'Luxembourg': 'geoTargetConstants/2442',
  // Northern Europe
  'Sweden': 'geoTargetConstants/2752',
  'Denmark': 'geoTargetConstants/2208',
  'Norway': 'geoTargetConstants/2578',
  'Finland': 'geoTargetConstants/2246',
  // Central/Eastern Europe
  'Poland': 'geoTargetConstants/2616',
  'Czech Republic': 'geoTargetConstants/2203',
  // North America
  'United States': 'geoTargetConstants/2840',
  'Canada': 'geoTargetConstants/2124',
  'Mexico': 'geoTargetConstants/2484',
  // South America
  'Brazil': 'geoTargetConstants/2076',
  'Argentina': 'geoTargetConstants/2032',
  'Colombia': 'geoTargetConstants/2170',
  'Chile': 'geoTargetConstants/2152',
  'Peru': 'geoTargetConstants/2604',
  // Asia Pacific
  'Japan': 'geoTargetConstants/2392',
  'South Korea': 'geoTargetConstants/2410',
  'Taiwan': 'geoTargetConstants/2158',
  'Hong Kong': 'geoTargetConstants/2344',
  'Singapore': 'geoTargetConstants/2702',
  'Australia': 'geoTargetConstants/2036',
  'New Zealand': 'geoTargetConstants/2554',
  'India': 'geoTargetConstants/2356',
  // Middle East
  'Saudi Arabia': 'geoTargetConstants/2682',
  'United Arab Emirates': 'geoTargetConstants/2784',
  'Kuwait': 'geoTargetConstants/2414',
  'Qatar': 'geoTargetConstants/2634',
  // Others
  'Russia': 'geoTargetConstants/2643',
  'South Africa': 'geoTargetConstants/2710',
};

// Currency codes
export const CURRENCIES = [
  'EUR', 'GBP', 'USD', 'CHF', 'SEK', 'DKK', 'NOK', 'PLN', 'CZK',
  'JPY', 'KRW', 'TWD', 'HKD', 'SGD', 'AUD', 'NZD', 'CAD',
  'MXN', 'BRL', 'ARS', 'CLP', 'COP', 'PEN',
  'SAR', 'AED', 'INR', 'RUB', 'ZAR'
];

// Language codes
export const LANGUAGES = [
  { code: 'es', name: 'Spanish' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pl', name: 'Polish' },
  { code: 'cs', name: 'Czech' },
  { code: 'sv', name: 'Swedish' },
  { code: 'da', name: 'Danish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ar', name: 'Arabic' },
];

export interface RegionalTemplate {
  id: string;
  name: string;
  marketCount: number;
  markets: Array<{
    language: string;
    location: string;
    locationCode: string;
    currency: string;
    multiplier: number;
    isRepresentative: boolean;
  }>;
}

export interface LanguageGroup {
  code: string;
  name: string;
  representative: {
    location: string;
    locationCode: string;
    currency: string;
  };
  countryCount: number;
  countries: Array<{
    location: string;
    currency: string;
    multiplier: number;
    isRepresentative: boolean;
  }>;
}

export interface KeywordEnrichmentStatus {
  enriched: number;
  pending: number;
  total: number;
  coverage: number;
  lastUpdated: string | null;
}

export interface CategoryDetailProduct {
  id: string;
  name: string;
  brand: string | null;
  productKeyword: string | null;
  categoryKeywords: Record<string, string>;
  estimatedCpc: number;
  estimatedSearches: number;
  searchValue: number;
  hasContent: boolean;
  dataSource: string | null;
  lastUpdated: string | null;
}

export interface CategoryDetailMarketBreakdown {
  key: string;
  language: string;
  location: string;
  currency: string;
  totalSearches: number;
  totalValue: number;
  productCount: number;
  categoryTerms: string[];
  valuePercentage: number;
}

export interface CategoryDetailResponse {
  category: string;
  market: {
    language: string;
    location: string;
    locationCode: string;
    currency: string;
    highDemandThreshold: number;
    lowDemandThreshold: number;
  };
  summary: {
    totalProducts: number;
    productsWithContent: number;
    productsWithKeywordData: number;
    totalSearchValue: number;
    readinessPct: number;
    keywordCoveragePct: number;
  };
  keywords: {
    productKeywords: string[];
    categoryKeywords: Record<string, string[]>;
  };
  marketBreakdown: CategoryDetailMarketBreakdown[];
  products: CategoryDetailProduct[];
  computation: {
    formula: string;
    cpcSource: string;
    categoryWeight: number;
    explanation: string[];
  };
}

class MarketsService {
  /**
   * Get keyword enrichment status for a site
   */
  async getKeywordEnrichmentStatus(siteId: string): Promise<KeywordEnrichmentStatus> {
    const response = await authFetch(`/api/factory/keyword-enrichment/status?siteId=${siteId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch keyword enrichment status');
    }
    return response.json();
  }

  /**
   * Trigger keyword enrichment for a site
   */
  async triggerKeywordEnrichment(siteId: string): Promise<{ message: string; markets: string; status: string }> {
    const response = await authFetch('/api/factory/keyword-enrichment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      throw new Error('Failed to trigger keyword enrichment');
    }
    return response.json();
  }

  /**
   * Get all markets configured for a site
   */
  async getSiteMarkets(siteId: string): Promise<Market[]> {
    const response = await authFetch(`/api/executive/site-markets?siteId=${siteId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch site markets');
    }
    const data = await response.json();
    return (data.markets || []).map((m: any) => ({
      id: m.id,
      language: m.language,
      location: m.location,
      locationCode: m.location_code,
      currency: m.currency,
      isPrimary: m.is_primary,
      highDemandThreshold: parseFloat(m.high_demand_threshold),
      lowDemandThreshold: parseFloat(m.low_demand_threshold),
      exchangeRateToPrimary: parseFloat(m.exchange_rate_to_primary || '1'),
    }));
  }

  /**
   * Add or update a market for a site
   */
  async saveMarket(siteId: string, market: Partial<Market>): Promise<Market> {
    const response = await authFetch('/api/executive/site-markets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        siteId,
        language: market.language,
        location: market.location,
        locationCode: market.locationCode || LOCATION_CODES[market.location || ''] || null,
        currency: market.currency,
        isPrimary: market.isPrimary,
        highDemandThreshold: market.highDemandThreshold,
        lowDemandThreshold: market.lowDemandThreshold,
        exchangeRateToPrimary: market.exchangeRateToPrimary,
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to save market');
    }
    return response.json();
  }

  /**
   * Delete a market
   */
  async deleteMarket(marketId: string): Promise<void> {
    const response = await authFetch(`/api/executive/site-markets/${marketId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete market');
    }
  }

  /**
   * Get category demand vs readiness data
   */
  async getCategoryDemandReadiness(siteId: string): Promise<DemandReadinessResponse> {
    const response = await authFetch(`/api/executive/category-demand-readiness?siteId=${siteId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch category demand readiness');
    }
    return response.json();
  }

  /**
   * Get detailed breakdown for a specific category
   */
  async getCategoryDetail(siteId: string, category: string): Promise<CategoryDetailResponse> {
    const response = await authFetch(`/api/executive/category-detail?siteId=${siteId}&category=${encodeURIComponent(category)}`);
    if (!response.ok) {
      throw new Error('Failed to fetch category detail');
    }
    return response.json();
  }

  /**
   * Get market opportunity summary for executive view
   */
  async getMarketOpportunitySummary(siteId: string): Promise<MarketOpportunitySummary> {
    const response = await authFetch(`/api/executive/market-opportunity?siteId=${siteId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch market opportunity');
    }
    return response.json();
  }

  /**
   * Get all available regional templates
   */
  async getRegionalTemplates(): Promise<RegionalTemplate[]> {
    const response = await authFetch('/api/executive/market-templates');
    if (!response.ok) {
      throw new Error('Failed to fetch regional templates');
    }
    const data = await response.json();
    return data.templates || [];
  }

  /**
   * Get all available language groups
   */
  async getLanguageGroups(): Promise<LanguageGroup[]> {
    const response = await authFetch('/api/executive/market-groups');
    if (!response.ok) {
      throw new Error('Failed to fetch language groups');
    }
    const data = await response.json();
    return data.groups || [];
  }

  /**
   * Get all countries for a specific language
   */
  async getLanguageCountries(language: string): Promise<{
    language: string;
    countries: Array<{
      location: string;
      currency: string;
      multiplier: number;
      locationCode: string;
      isRepresentative: boolean;
    }>;
  }> {
    const response = await authFetch(`/api/executive/language-countries/${language}`);
    if (!response.ok) {
      throw new Error('Failed to fetch language countries');
    }
    return response.json();
  }

  /**
   * Add markets to a site from a regional template
   */
  async addMarketsFromTemplate(siteId: string, templateId: string, primaryLanguage?: string): Promise<{
    message: string;
    added: number;
    failed: number;
    results: Array<{
      language: string;
      location: string;
      added: boolean;
      error?: string;
    }>;
  }> {
    const response = await authFetch('/api/executive/site-markets/from-template', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, templateId, primaryLanguage }),
    });
    if (!response.ok) {
      throw new Error('Failed to add markets from template');
    }
    return response.json();
  }
}

export const marketsService = new MarketsService();
